/**
 * Tooltip copy for the Overview Panel's metric rows. Keyed by the same field
 * names as `OverviewMetrics` (see `data/impactTypes.ts`) so a metric and its
 * tooltip can't drift apart.
 *
 * `hint` is the short one-line blurb shown above the fuller description;
 * omitted where none was provided (e.g. Median household income). `source`
 * is omitted for a metric that only has one line of copy, not a full
 * citation — see `TooltipBody`, which renders whichever of
 * hint/description/source are present.
 */

export interface MetricTooltip {
  hint?: string;
  description: string;
  source?: string;
}

export type OverviewMetricKey =
  | 'eligibleChildrenUnder18'
  | 'medianHouseholdIncome'
  | 'participation529'
  | 'childPovertyRate'
  | 'economicMobility'
  | 'childrenReceivingSNAP'
  | 'housingBurdened'
  | 'needleScore'
  | 'opportunityScore';

export const OVERVIEW_METRIC_TOOLTIPS: Record<OverviewMetricKey, MetricTooltip> = {
  eligibleChildrenUnder18: {
    hint: '% of children under 18 in this area',
    description:
      'Total number of children living in searched area between birth and age 17.',
    source: "U.S. Census Bureau's American Community Survey (ACS 5-Year 2024)",
  },
  medianHouseholdIncome: {
    description: 'Median household income in dollars.',
    source: "U.S. Census Bureau's American Community Survey (ACS 5-Year 2024)",
  },
  participation529: {
    hint: 'Estimated share of families in this area with an active 529 college savings account',
    description:
      'Estimated percentage of households currently saving in a 529 college savings plan.',
    source:
      "Sallie Mae 'How America Saves for College' 2024; U.S. Census Bureau's American Community Survey (ACS 5-Year 2024); SIPP Data 2025; Survey of Consumer Finances 2022",
  },
  childPovertyRate: {
    hint: 'Percentage of children under 18 living below the federal poverty line',
    description:
      'Percentage of children under 18 living in households with income below the federal poverty line.',
    source: "U.S. Census Bureau's American Community Survey (ACS 5-Year 2024)",
  },
  economicMobility: {
    hint: "A child's likelihood of earning more than their parents",
    description:
      'Measures how economically successful low-income children who grew up in the area became as adults, based on their average household income rank at approximately age 35.',
    source: 'Opportunity Atlas',
  },
  childrenReceivingSNAP: {
    hint: 'Share of children enrolled in the Supplemental Nutrition Assistance Program (SNAP)',
    description:
      'Percentage of households with children under 18 that received Supplemental Nutrition Assistance Program (SNAP) benefits — formerly known as food stamps — at any point in the past 12 months.',
    source: "U.S. Census Bureau's American Community Survey (ACS 5-Year 2024)",
  },
  housingBurdened: {
    hint: 'Percentage of households spending more than 30% of their income on housing',
    description:
      'Percentage of renter-occupied and owner-occupied households that spend 30 percent or more of household income on gross rent or selected monthly owner costs.',
    source: "U.S. Census Bureau's American Community Survey (ACS 5-Year 2024)",
  },
  needleScore: {
    description: "Share of a child's expected savings at 18 that the philanthropic seed covers.",
    source:
      "LS Technologies model using estimated expected 529 savings at 18 and philanthropic contributions' savings at 18",
  },
  opportunityScore: {
    description:
      'Score based on LS Technologies model using eligible children and child opportunity index.',
    source:
      "U.S. Census Bureau's American Community Survey (ACS 5-Year 2024); Diversity Data Kids (2024)",
  },
};
